import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import EvidenceCard from "../components/EvidenceCard";
import Marquee from "../components/Marquee";

const ACCENT = "#FF3B30";

const BIZ_IMG =
  "https://images.unsplash.com/photo-1773266110858-acb9b6c43b15?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const EPA_IMG =
  "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const BANK_IMG =
  "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export default function ImpactBusinesses() {
  return (
    <div style={{ backgroundColor: "#050505", paddingTop: "64px" }}>
      {/* Header */}
      <div
        data-testid="businesses-header"
        style={{
          padding: "64px 24px 56px",
          borderBottom: "1px solid #1A1A1A",
          backgroundColor: "#0A0A0A",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <p
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: "11px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: ACCENT,
              marginBottom: "16px",
            }}
          >
            Page 03 — Evidence
          </p>
          <h1
            style={{
              fontFamily: "'Cabinet Grotesk', sans-serif",
              fontSize: "clamp(2rem, 5vw, 3.8rem)",
              fontWeight: 900,
              letterSpacing: "-0.03em",
              color: "#FFFFFF",
              marginBottom: "16px",
              lineHeight: 1.05,
              borderLeft: `4px solid ${ACCENT}`,
              paddingLeft: "24px",
            }}
          >
            Impact on Businesses
          </h1>
          <p
            style={{
              fontSize: "16px",
              color: "#71717A",
              maxWidth: "680px",
              lineHeight: 1.65,
              paddingLeft: "28px",
            }}
          >
            Government regulations impose real costs on businesses — but they
            also create a level playing field, protect industries from
            catastrophic failure, and can even spark innovation.
          </p>
        </div>
      </div>

      <Marquee />

      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "64px 24px",
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          gap: "64px",
          alignItems: "start",
        }}
        className="impact-layout"
      >
        {/* Sticky Commentary */}
        <aside
          data-testid="businesses-commentary"
          style={{ position: "sticky", top: "88px" }}
        >
          <p
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: "10px",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#52525B",
              marginBottom: "16px",
            }}
          >
            Student Commentary
          </p>
          <p
            style={{
              color: "#D1D1D6",
              fontSize: "14px",
              lineHeight: 1.8,
              marginBottom: "20px",
            }}
          >
            When I started researching this topic, I expected the answer to be
            simple — regulation either helps or it hurts. But the reality is
            much more nuanced. Regulations that protect the environment can
            drive up energy costs for businesses. Financial rules that prevent
            another 2008 crash also make it harder for small banks to compete.
          </p>
          <p
            style={{
              color: "#D1D1D6",
              fontSize: "14px",
              lineHeight: 1.8,
              marginBottom: "20px",
            }}
          >
            What I found most interesting was how the burden of regulation
            falls disproportionately on small businesses. A large corporation
            can hire a compliance department. A small business owner has to
            figure it out themselves.
          </p>
          <p
            style={{
              color: "#D1D1D6",
              fontSize: "14px",
              lineHeight: 1.8,
            }}
          >
            This connects to a broader economics concept: regulations often
            have unintended consequences that affect the most vulnerable
            businesses and workers the most.
          </p>

          <div style={{ marginTop: "36px", padding: "20px", backgroundColor: "#0D0D0D", border: "1px solid #1A1A1A" }}>
            <p
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: "10px",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#52525B",
                marginBottom: "12px",
              }}
            >
              Research Question
            </p>
            <p style={{ color: "#A1A1AA", fontSize: "13px", lineHeight: 1.7, fontStyle: "italic" }}>
              "How does government regulation affect businesses, workers, and
              consumers in the U.S. economy?"
            </p>
          </div>
        </aside>

        {/* Evidence Cards */}
        <div data-testid="businesses-evidence">
          <EvidenceCard
            artifactNum={1}
            title="The Heavy Price of Compliance: Regulatory Costs on U.S. Businesses"
            imageUrl={BIZ_IMG}
            imageAlt="Business financial data and charts"
            accentColor={ACCENT}
            caption="Regulatory compliance costs create a significant and growing burden — especially for smaller firms that lack dedicated legal and compliance departments."
            citation="[1]"
          >
            <p style={{ marginBottom: "14px" }}>
              According to the National Association of Manufacturers, U.S.
              businesses spend approximately{" "}
              <strong style={{ color: "#FFFFFF" }}>$1.9 trillion annually</strong>{" "}
              on regulatory compliance — roughly equivalent to all individual
              income taxes collected in a year. For small businesses, this
              burden is especially disproportionate: they spend approximately{" "}
              <strong style={{ color: "#FFFFFF" }}>36% more per employee</strong>{" "}
              on compliance compared to large corporations.
              <sup style={{ color: ACCENT }}>[1]</sup>
            </p>
            <p>
              This creates what economists call a "regulatory burden," where
              compliance costs cut directly into profits and limit a business's
              ability to hire, invest, or expand. A 2023 SBA report found that
              the total cost of federal regulations to the U.S. economy exceeds
              $3 trillion when indirect costs are included — a massive number
              that shapes how every business makes decisions.
            </p>
          </EvidenceCard>

          <EvidenceCard
            artifactNum={2}
            title="EPA Clean Air Regulations and the Energy Sector"
            imageUrl={EPA_IMG}
            imageAlt="Power plant and environmental regulations"
            accentColor={ACCENT}
            caption="EPA emissions standards have reshaped the U.S. energy industry, forcing coal companies to invest in pollution controls or shut down entirely."
            citation="[2]"
          >
            <p style={{ marginBottom: "14px" }}>
              The Environmental Protection Agency's Clean Air Act regulations
              have required energy companies to invest billions in pollution
              control technology. According to the Energy Information
              Administration, compliance with EPA standards contributed to the
              closure of{" "}
              <strong style={{ color: "#FFFFFF" }}>
                over 100 coal power plants
              </strong>{" "}
              between 2010 and 2022.
              <sup style={{ color: ACCENT }}>[2]</sup>
            </p>
            <p style={{ marginBottom: "14px" }}>
              For affected companies and their shareholders, this was a
              devastating financial blow. But the EPA argues — with supporting
              data — that the benefits of cleaner air far outweigh the costs.
              Their own analysis of the Clean Air Act estimates benefits worth
              over <strong style={{ color: "#FFFFFF" }}>$30 for every $1</strong>{" "}
              spent on compliance.
            </p>
            <p>
              This case illustrates a core tension: regulation that protects
              the public good can harm specific industries, regions, and the
              workers who depend on them. The coal communities of Appalachia
              are a real-world example of who bears those costs.
            </p>
          </EvidenceCard>

          <EvidenceCard
            artifactNum={3}
            title="Dodd-Frank and the Financial Services Industry"
            imageUrl={BANK_IMG}
            imageAlt="Bank building representing financial regulation"
            accentColor={ACCENT}
            caption="The Dodd-Frank Act of 2010 imposed sweeping new requirements on financial institutions, reshaping how banks operate across the United States."
            citation="[3]"
          >
            <p style={{ marginBottom: "14px" }}>
              After the 2008 financial crisis wiped out trillions in household
              wealth and triggered the worst recession since the Great
              Depression, Congress passed the Dodd-Frank Wall Street Reform and
              Consumer Protection Act in 2010. The law required large banks to
              hold more capital reserves (tested through annual "stress tests"),
              banned certain types of risky trading, and created extensive new
              reporting requirements.
              <sup style={{ color: ACCENT }}>[3]</sup>
            </p>
            <p style={{ marginBottom: "14px" }}>
              The American Bankers Association estimates Dodd-Frank compliance
              costs community banks roughly{" "}
              <strong style={{ color: "#FFFFFF" }}>$4.5 billion annually</strong>.
              Critics argue this has disadvantaged smaller banks relative to
              large institutions — which can spread compliance costs over a
              much larger asset base — and reduced access to credit for some
              consumers and small businesses.
            </p>
            <p>
              Supporters counter that these regulations directly prevented
              another catastrophic financial collapse. Since 2010, no major
              U.S. bank has failed due to the type of reckless risk-taking that
              caused the 2008 crisis — which they attribute directly to these
              regulatory safeguards.
            </p>
          </EvidenceCard>

          {/* Next Page */}
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "16px" }}>
            <Link
              to="/impact-workers"
              data-testid="next-page-workers"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#FFCC00",
                color: "#050505",
                padding: "12px 24px",
                fontSize: "13px",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              Next: Impact on Workers <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .impact-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
